import {useCallback, useEffect, useMemo, useState} from 'react';
import {
    addReaction,
    createReply,
    deleteMessage,
    listReplies,
    removeReaction,
    updateMessage,
    updateThreadState,
} from '../../../service/ChatService';
import {mergeMessages} from '../chatUtils';

export default function useThreadReplies({conversation, root, currentUser, invalidation, onRootChanged}) {
    const [replies, setReplies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState(null);
    const conversationId = conversation.id;
    const rootId = root.id;

    const refresh = useCallback(async (preserve = true) => {
        const payload = await listReplies(conversationId, rootId);
        const incoming = payload.messages || [];
        setReplies(current => preserve ? mergeMessages(current, incoming) : incoming);
        setHasMore(incoming.length === 50);
        return incoming;
    }, [conversationId, rootId]);

    const markRead = useCallback(async throughSequence => {
        await updateThreadState(rootId, {following: true, last_read_reply_sequence: throughSequence});
        await onRootChanged();
    }, [rootId, onRootChanged]);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setReplies([]);
        setError(null);
        refresh(false).then(messages => {
            if (!active) return;
            return markRead(messages.at(-1)?.sequence || root.sequence);
        }).catch(setError).finally(() => active && setLoading(false));
        return () => { active = false; };
    }, [refresh, markRead, root.sequence]);

    useEffect(() => {
        if (!invalidation) return;
        refresh(true).then(messages => {
            const through = messages.at(-1)?.sequence;
            if (through && document.visibilityState === 'visible') return markRead(through);
            return undefined;
        }).catch(setError);
    }, [invalidation, refresh, markRead]);

    const loadOlder = useCallback(async () => {
        const first = replies[0]?.sequence;
        if (!first) return;
        try {
            const payload = await listReplies(conversationId, rootId, {beforeSequence: first});
            const older = payload.messages || [];
            setReplies(current => mergeMessages(older, current));
            setHasMore(older.length === 50);
        } catch (loadError) {
            setError(loadError);
        }
    }, [conversationId, rootId, replies]);

    const sendReply = useCallback(async (body, attachments = [], retryId) => {
        const clientId = retryId || crypto.randomUUID();
        const pendingId = `pending-${clientId}`;
        setReplies(current => {
            const pending = {
                id: pendingId,
                conversation_id: conversationId,
                sequence: (current.at(-1)?.sequence || root.sequence) + 1,
                sender: currentUser,
                message_type: 'USER',
                body,
                attachments,
                reactions: [],
                pending: true,
                client_message_id: clientId,
                created_at: new Date().toISOString(),
            };
            return [...current.filter(item => item.id !== pendingId), pending];
        });
        try {
            const result = await createReply(conversationId, rootId, body, clientId, attachments.map(attachment => attachment.id));
            setReplies(current => mergeMessages(current.filter(item => item.id !== pendingId), [result.message]));
            await markRead(result.message.sequence);
        } catch (sendError) {
            setReplies(current => current.map(item => item.id === pendingId ? {...item, pending: false, send_error: true, sendError} : item));
            throw sendError;
        }
    }, [conversationId, rootId, root.sequence, currentUser, markRead]);

    const changeMessage = useCallback(async action => {
        await action();
        await refresh(false);
        await onRootChanged();
    }, [refresh, onRootChanged]);

    const actions = useMemo(() => ({
        edit: (message, body) => changeMessage(() => updateMessage(message.id, body, message.version)),
        remove: message => changeMessage(() => deleteMessage(message.id)),
        react: (message, emoji, reacted) => changeMessage(() => reacted ? removeReaction(message.id, emoji) : addReaction(message.id, emoji)),
        retry: message => sendReply(message.body, message.attachments || [], message.client_message_id),
    }), [changeMessage, sendReply]);

    return {replies, loading, hasMore, error, loadOlder, sendReply, actions};
}
